import { ORDER_STATUS, type OrderStatus } from '../constants/orderStatuses';

// ── Per-status metadata ──────────────────────────────────────────────────────
//
// Record<OrderStatus, StatusMeta> requires an entry for every status in
// ORDER_STATUS. If someone adds a new status and forgets to add a row here,
// this file fails to compile — that's the whole point. It replaces the old
// `currentSortOrder >= 4` / `currentSortOrder === 9` style checks in
// OrderDetails.tsx, which silently kept working (with the wrong answer) every
// time a new status shifted the numbers around.

interface StatusMeta {
    /**
     * True for the payment-attempt stage — everything from order creation up
     * to (but not including) AWAITING_PACKING. Mirrors the old
     * `currentSortOrder < 9` check.
     */
    isPrePacking: boolean;

    /**
     * True once the payment attempt actually succeeded (pre-auth confirmed) —
     * this is what gates whether packing/fulfilment buttons show up.
     * PAYMENT_FAILED / PAYMENT_CANCELLED / PAYMENT_DECLINED / PAYMENT_VOIDED
     * are dead-end failure branches: even though they sit numerically after
     * PAYMENT_SUCCEEDED in sort_order, the payment never actually went
     * through, so this is false for them.
     */
    hasBeenPaid: boolean;
}

const ORDER_STATUS_META: Record<OrderStatus, StatusMeta> = {
    [ORDER_STATUS.CREATED]:                { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.AWAITING_PAYMENT]:       { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.PAYMENT_PRE_AUTHORIZED]: { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.PAYMENT_SUCCEEDED]:      { isPrePacking: true,  hasBeenPaid: true  },
    [ORDER_STATUS.PAYMENT_FAILED]:         { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.PAYMENT_CANCELLED]:      { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.PAYMENT_DECLINED]:       { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.PAYMENT_VOIDED]:         { isPrePacking: true,  hasBeenPaid: false },
    [ORDER_STATUS.AWAITING_PACKING]:       { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.PACKER_ASSIGNED]:        { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.PACKING_COMPLETE]:       { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.PAYMENT_PROCESSING]:     { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.CAPTURE_FAILED]:         { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.READY_FOR_COLLECTION]:   { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.COLLECTION_COMPLETE]:    { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.ASSIGN_DRIVER]:          { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.DRIVER_ASSIGNED]:        { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.ON_ROUTE]:               { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.DELIVERY_FAILED]:        { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.DELIVERY_COMPLETE]:      { isPrePacking: false, hasBeenPaid: true  },
    [ORDER_STATUS.ORDER_CANCELLED]:        { isPrePacking: false, hasBeenPaid: true  },
};

const DEFAULT_META: StatusMeta = { isPrePacking: false, hasBeenPaid: false };

// Falls back to a safe default for any status not present in the table above —
// most commonly the transient '' status before currentOrder has loaded, but
// also guards against any unexpected/invalid status string reaching here.
function getMeta(status: OrderStatus): StatusMeta {
    return ORDER_STATUS_META[status] ?? DEFAULT_META;
}

const isPrePacking       = (status: OrderStatus): boolean => getMeta(status).isPrePacking;
export const hasBeenPaid = (status: OrderStatus): boolean => getMeta(status).hasBeenPaid;

// ── Fulfilment action visibility ─────────────────────────────────────────────
// Each of these replaces one `sort_order === N` / `[N, N, N].includes(...)`
// check from OrderDetails.tsx with a named, order-independent equivalent.

/** Was: `currentSortOrder === 9 || (isChatType && currentSortOrder < 9)` */
export const canShowPrintButton = (status: OrderStatus, isChatType: boolean): boolean =>
    status === ORDER_STATUS.AWAITING_PACKING || (isChatType && isPrePacking(status));

/** Was: `currentSortOrder === 9` */
export const canAssignPacker = (status: OrderStatus): boolean =>
    status === ORDER_STATUS.AWAITING_PACKING;

/** Was: `currentSortOrder === 10` */
export const canUnassignPicker = (status: OrderStatus): boolean =>
    status === ORDER_STATUS.PACKER_ASSIGNED;

const DELIVERY_ELIGIBLE_STATUSES: OrderStatus[] = [
    ORDER_STATUS.ASSIGN_DRIVER,
    ORDER_STATUS.DRIVER_ASSIGNED,
];

/**
 * Was: `!isCollectionOrder && [11, 16, 17].includes(currentSortOrder)`
 * (11 = PACKING_COMPLETE). That let staff create a delivery before the
 * post-packing capture (PAYMENT_PROCESSING → success/CAPTURE_FAILED) had
 * even run. Capture only succeeds once the order reaches ASSIGN_DRIVER
 * (the backend advances it there on a confirmed capture) — so PACKING_COMPLETE
 * is dropped from the eligible list for card-paid orders.
 * Chat orders are paid manually with no capture step, so they keep the
 * earlier PACKING_COMPLETE eligibility.
 */
export const canShowDeliveryButton = (status: OrderStatus, isCollectionOrder: boolean, isChatType: boolean): boolean => {
    if (isCollectionOrder) return false;
    if (isChatType) return status === ORDER_STATUS.PACKING_COMPLETE || DELIVERY_ELIGIBLE_STATUSES.includes(status);
    return DELIVERY_ELIGIBLE_STATUSES.includes(status);
};

const COMPLETE_COLLECTION_ELIGIBLE_STATUSES: OrderStatus[] = [
    ORDER_STATUS.READY_FOR_COLLECTION,
];

/**
 * Was: `isCollectionOrder && (currentSortOrder === 11 || currentSortOrder === 14)`
 * (11 = PACKING_COMPLETE, 14 = READY_FOR_COLLECTION). That let staff mark a
 * collection order ready before the post-packing capture had confirmed.
 * settlePickedOrder → advanceToAssignDriver (settle_order.ts) already moves a
 * collection order to READY_FOR_COLLECTION automatically once capture
 * succeeds, so PACKING_COMPLETE is dropped from the eligible list here too —
 * mirrors canShowDeliveryButton's fix. Chat orders skip the capture step, so
 * they keep the earlier PACKING_COMPLETE eligibility.
 */
export const canShowCompleteCollectionButton = (status: OrderStatus, isCollectionOrder: boolean, isChatType: boolean): boolean => {
    if (!isCollectionOrder) return false;
    if (isChatType) return status === ORDER_STATUS.PACKING_COMPLETE || COMPLETE_COLLECTION_ELIGIBLE_STATUSES.includes(status);
    return COMPLETE_COLLECTION_ELIGIBLE_STATUSES.includes(status);
};

/** Was: `currentSortOrder === 11 ? 'Ready for Collection' : 'Complete Collection'` */
export const getCompleteCollectionLabel = (status: OrderStatus): string =>
    status === ORDER_STATUS.PACKING_COMPLETE ? 'Ready for Collection' : 'Complete Collection';

/** Was: `(hasBeenPaid || isChatType) && !isArchived` — now uses the corrected hasBeenPaid */
export const showFulfillmentActions = (status: OrderStatus, isChatType: boolean, isArchived: boolean): boolean =>
    (hasBeenPaid(status) || isChatType) && !isArchived;

const PRE_PAYMENT_CONFIRMATION_STATUSES: OrderStatus[] = [
    ORDER_STATUS.CREATED,
    ORDER_STATUS.AWAITING_PAYMENT,
    ORDER_STATUS.PAYMENT_PRE_AUTHORIZED,
];

/** Was: `currentSortOrder < 4 ? 'Awaiting Payment' : isArchived ? 'Order Finalized/Cancelled' : 'Processed'` */
export const getPaymentStageLabel = (status: OrderStatus, isArchived: boolean): string => {
    if (PRE_PAYMENT_CONFIRMATION_STATUSES.includes(status)) return 'Awaiting Payment';
    return isArchived ? 'Order Finalized/Cancelled' : 'Processed';
};